import type { ArtifactCopyWire, ArtifactStateWire, AssetManifest } from '@kenocyber/shared';
import { useMemo, useState } from 'react';
import { staticAssetUrl } from '../game/assets';
import { artifactEffectLines } from './artifact-format';
import { CellBadges } from './CellBadges';
import { rarityLabel, rarityPaint, rarityStyle, rarityTierOf } from './rarity';

/**
 * The artifacts the account owns, and what each one is doing.
 *
 * Artifacts are NOT equipment: there is no slot, no Equipar button and no way
 * to turn one off. Every artifact a player owns contributes at the same time -
 * but only the STRONGEST copy of each, which is the one fact this screen has
 * to make unmissable. Three copies of Guns are not three bonuses, and a UI
 * that let a player believe otherwise would be lying about their damage.
 */

interface ArtifactsTabProps {
  readonly artifacts: readonly ArtifactStateWire[];
  readonly manifest: AssetManifest;
  readonly selectedId: string | null;
  readonly onSelect: (definitionId: string) => void;
}

/** Owned first, then rarity, then level: what the player has, up front. */
function compareArtifacts(a: ArtifactStateWire, b: ArtifactStateWire): number {
  const ownedA = a.copies.length > 0;
  const ownedB = b.copies.length > 0;
  if (ownedA !== ownedB) {
    return ownedA ? -1 : 1;
  }
  const tier = rarityTierOf(b.rarity) - rarityTierOf(a.rarity);
  if (tier !== 0) {
    return tier;
  }
  return (b.effectiveLevel ?? 0) - (a.effectiveLevel ?? 0);
}

export function ArtifactsTab({ artifacts, manifest, selectedId, onSelect }: ArtifactsTabProps) {
  const ordered = useMemo(() => [...artifacts].sort(compareArtifacts), [artifacts]);

  return (
    <div className="art-grid" data-testid="artifacts-grid">
      {ordered.map((artifact) => {
        const owned = artifact.copies.length > 0;
        const url = staticAssetUrl(manifest, artifact.assetKey);
        return (
          <button
            key={artifact.definitionId}
            type="button"
            className="inv-cell art-cell"
            // The rarity border is a property of the cell, exactly as it is in
            // the item grid: owned, selected and hovered all layer ON it.
            data-rarity={artifact.rarity}
            data-paint={rarityPaint(artifact.rarity)}
            data-owned={owned ? 'true' : 'false'}
            data-selected={artifact.definitionId === selectedId ? 'true' : 'false'}
            data-testid={`art-cell-${artifact.definitionId}`}
            style={rarityStyle(artifact.rarity)}
            aria-pressed={artifact.definitionId === selectedId}
            aria-label={`${artifact.name}, ${rarityLabel(artifact.rarity)}${
              owned ? `, nível ${artifact.effectiveLevel}` : ', não obtido'
            }`}
            onClick={() => onSelect(artifact.definitionId)}
          >
            <span className="inv-cell-ring" aria-hidden="true" />
            <span className="inv-cell-art">
              {/* `contain` in a box that is 99% of the cell: the 1254x1254
                  master fills the square, keeps its transparency, and is
                  never cropped or stretched. */}
              {url ? (
                <img src={url} alt="" loading="lazy" draggable={false} />
              ) : (
                <span className="inv-cell-noart" aria-hidden="true">
                  ?
                </span>
              )}
            </span>
            {owned ? (
              // This tab GROUPS the copies of one artifact into a single cell,
              // so the quantity is the real count - `×2` when two are owned.
              // The level shown is the one actually granting the passive.
              <CellBadges
                level={artifact.effectiveLevel ?? 1}
                quantity={artifact.copies.length}
                rarity={artifact.rarity}
                testIdSuffix={artifact.definitionId}
              />
            ) : (
              <span
                className="art-cell-missing"
                data-testid={`art-missing-${artifact.definitionId}`}
              >
                não obtido
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

interface ArtifactDetailsProps {
  readonly artifact: ArtifactStateWire | null;
  readonly manifest: AssetManifest;
  /** The pair currently being merged, so the button can disable itself. */
  readonly combining: boolean;
  readonly error: string | null;
  readonly onCombine: (first: string, second: string) => void;
}

/** The two copies a merge would spend: same level, lowest first, never reserved. */
export function firstCombinablePair(
  artifact: ArtifactStateWire,
): readonly [ArtifactCopyWire, ArtifactCopyWire] | null {
  const byLevel = new Map<number, ArtifactCopyWire[]>();
  for (const copy of artifact.copies) {
    if (copy.level >= artifact.maxLevel) {
      // Level 10 is the end of the line: two of them combine into nothing.
      continue;
    }
    if (copy.reserved) {
      // A copy promised to a listing or a trade is never auto-selected: it is
      // not the player's to spend until the promise is resolved.
      continue;
    }
    const bucket = byLevel.get(copy.level);
    if (bucket) {
      bucket.push(copy);
    } else {
      byLevel.set(copy.level, [copy]);
    }
  }
  // The LOWEST level with a pair: merging the weakest duplicates first is the
  // move that costs the player nothing they are currently benefiting from.
  for (const level of [...byLevel.keys()].sort((a, b) => a - b)) {
    const bucket = byLevel.get(level) ?? [];
    const [first, second] = bucket;
    if (first && second) {
      return [first, second];
    }
  }
  return null;
}

function EffectList({
  title,
  lines,
  testId,
}: {
  title: string;
  lines: readonly { key: string; label: string; value: string }[];
  testId: string;
}) {
  if (lines.length === 0) {
    return null;
  }
  return (
    <div className="art-effects" data-testid={testId}>
      <h4>{title}</h4>
      {lines.map((line) => (
        <div key={line.key} className="inv-detail-row">
          <span className="inv-detail-label">{line.label}</span>
          <span className="inv-detail-value">{line.value}</span>
        </div>
      ))}
    </div>
  );
}

export function ArtifactDetails({
  artifact,
  manifest,
  combining,
  error,
  onCombine,
}: ArtifactDetailsProps) {
  const [confirming, setConfirming] = useState(false);

  if (!artifact) {
    return (
      <aside className="inv-details" data-testid="artifact-details">
        <p className="inv-state">Selecione um artefato.</p>
      </aside>
    );
  }

  const owned = artifact.copies.length > 0;
  const url = staticAssetUrl(manifest, artifact.assetKey);
  const pair = firstCombinablePair(artifact);
  const current = artifactEffectLines(owned ? artifact.effects : artifact.level1Effects);
  const next = artifact.nextLevelEffects ? artifactEffectLines(artifact.nextLevelEffects) : [];

  return (
    <aside
      className="inv-details"
      data-testid="artifact-details"
      data-definition-id={artifact.definitionId}
      style={rarityStyle(artifact.rarity)}
    >
      {/* Fixed header: art and identity, always in view. */}
      <header className="inv-details-head">
        <div
          className="inv-details-art"
          data-rarity={artifact.rarity}
          data-paint={rarityPaint(artifact.rarity)}
        >
          {url ? <img src={url} alt="" draggable={false} /> : <span aria-hidden="true">?</span>}
        </div>
        <div className="inv-details-title">
          <h3 data-testid="art-detail-name">{artifact.name}</h3>
          <span className="inv-details-sub" data-testid="art-detail-rarity">
            {rarityLabel(artifact.rarity)}
          </span>
          <span className="inv-details-sub">
            <span data-testid="art-detail-level">
              {owned ? `${artifact.effectiveLevel} / ${artifact.maxLevel}` : '—'}
            </span>
            {' · '}
            <span data-testid="art-detail-copies">{artifact.copies.length}</span>
            {artifact.copies.length === 1 ? ' cópia' : ' cópias'}
          </span>
        </div>
      </header>

      {/* Only this middle region ever scrolls. */}
      <div className="inv-details-body">
        {/* Artifacts are never equipped, so the screen says so instead of
            offering a button that could not exist. */}
        <p className="art-passive-note" data-testid="art-passive-note">
          {owned
            ? 'Passiva sempre ativa. Artefatos não ocupam slot e não são equipados.'
            : 'Ainda não obtido. Artefatos caem de inimigos derrotados.'}
        </p>

        <EffectList
          title={owned ? 'Efeitos atuais' : 'Efeitos no nível 1'}
          lines={current}
          testId="art-current-effects"
        />
        <EffectList title="No próximo nível" lines={next} testId="art-next-effects" />

        {artifact.copies.length > 0 ? (
          <div className="art-copies" data-testid="art-copy-list">
            <h4>Cópias</h4>
            {artifact.copies.map((copy) => (
              <div
                key={copy.instanceId}
                className="inv-detail-row"
                data-testid={`art-copy-${copy.instanceId}`}
              >
                <span className="inv-detail-label">Nível {copy.level}</span>
                <span
                  className="inv-detail-value"
                  data-active={copy.passiveActive ? 'true' : 'false'}
                >
                  {copy.passiveActive ? (
                    <strong data-testid={`art-copy-active-${copy.instanceId}`}>
                      PASSIVA ATIVA
                    </strong>
                  ) : (
                    <em data-testid={`art-copy-inert-${copy.instanceId}`}>
                      CÓPIA — BÔNUS NÃO ACUMULADO
                    </em>
                  )}
                  {copy.reserved ? ' · reservada' : ''}
                </span>
              </div>
            ))}
          </div>
        ) : null}

        {/* The Baby Alien chance is transported and displayed, and nothing
            else. There is no Baby Alien to receive: no definition, no art,
            no drop. */}
        {artifact.effects.babyAlienChanceMicroPp !== '0' ||
        artifact.level1Effects.babyAlienChanceMicroPp !== '0' ? (
          <p className="art-baby-alien" data-testid="art-baby-alien-note">
            O sistema de Baby Aliens ainda não foi lançado. Este bônus é registrado e exibido, mas
            nenhum Baby Alien existe no jogo hoje.
          </p>
        ) : null}
      </div>

      {/*
        Fixed action footer.

        It sits outside the scrolling region on purpose. Before this, COMBINAR
        was the last thing in a long scrolling column: a player with several
        copies had to scroll past the effects and the copy list to discover
        that combining was even possible. An action is not available if you
        have to go looking for it.
      */}
      <footer className="inv-details-actions" data-testid="artifact-actions">
        {error ? (
          <p className="inv-detail-error" role="alert" data-testid="art-combine-error">
            {error}
          </p>
        ) : null}

        <div className="inv-detail-row">
          <span className="inv-detail-label">Combinações possíveis</span>
          <span className="inv-detail-value" data-testid="art-combinable-pairs">
            {artifact.combinablePairs}
          </span>
        </div>

        {pair === null ? null : confirming ? (
          <div className="inv-confirm" data-testid="art-combine-confirm">
            <p>
              Combinar 2× nível {pair[0].level} em 1× nível {pair[0].level + 1}?
            </p>
            <p className="art-combine-warning">As duas cópias são consumidas.</p>
            <div className="inv-confirm-actions">
              <button
                type="button"
                data-testid="art-combine-confirm-yes"
                // Disabled the instant it is pressed: a double click cannot
                // become two requests, and the server's answer decides anyway.
                disabled={combining}
                onClick={() => {
                  setConfirming(false);
                  onCombine(pair[0].instanceId, pair[1].instanceId);
                }}
              >
                {combining ? 'Combinando…' : 'Confirmar'}
              </button>
              <button
                type="button"
                data-testid="art-combine-cancel"
                onClick={() => setConfirming(false)}
              >
                Cancelar
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="inv-equip"
            data-testid="art-combine"
            disabled={combining}
            onClick={() => setConfirming(true)}
          >
            {combining
              ? 'Combinando…'
              : `COMBINAR 2× Nv.${pair[0].level} → 1× Nv.${pair[0].level + 1}`}
          </button>
        )}
      </footer>
    </aside>
  );
}
